import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/auth';
import { apiService } from '@/services/api';
import { consumeReturnTo } from '@/utils/returnTo';
import { WaveLoader } from '@/components/ui/WaveLoader';

const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthenticatedUser } = useAuthStore();

  useEffect(() => {
    const handleAuthCallback = async () => {
      // SECURITY FIX: Extract authorization code (not JWT token) from URL
      // Backend now uses OAuth authorization code flow to prevent token leakage in URL
      const code = searchParams.get('code');

      if (!code) {
        if (import.meta.env.DEV) {
          console.error('No authorization code in OAuth callback URL');
        }
        navigate('/login', { replace: true });
        return;
      }

      // Exchange authorization code for JWT token via POST (not GET)
      let token: string;
      try {
        const response = await apiService.post<{ token: string }>('/auth/token', { code });
        token = (response.data.data as { token: string }).token;

        if (!token) {
          throw new Error('No token in response');
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to exchange code for token:', error);
        }
        navigate('/login', { replace: true });
        return;
      }

      // HIPAA: Store the JWT token in sessionStorage (clears on browser close)
      sessionStorage.setItem('authToken', token);

      // Fetch and store user data
      let user;
      try {
        user = await authService.getCurrentUser();
        sessionStorage.setItem('user', JSON.stringify(user));
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to fetch user data:', error);
        }
        // If we can't get user data, clear token and redirect to login
        sessionStorage.removeItem('authToken');
        navigate('/login', { replace: true });
        return;
      }

      // Directly set the authenticated user in the store (bypasses initialize guard)
      setAuthenticatedUser(user);

      // Restore the pre-logout deposit view if this login followed an
      // involuntary session timeout; otherwise go straight to /deposits
      // (avoids the /-> /deposits double-hop that would drop the query).
      navigate(consumeReturnTo() ?? '/deposits', { replace: true });
    };

    handleAuthCallback();
  }, [setAuthenticatedUser, navigate, searchParams]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <WaveLoader size="xl" className="mx-auto" />
      </div>
    </div>
  );
};

export default OAuthCallback;
